import { type UseMutationResult } from '@tanstack/react-query';

import { useApiMutation } from '../../../lib/api/hooks.js';
import { updatePayload, type PayloadSpec } from '../../../lib/forms/payload.js';
import { guests } from './endpoints.js';
import { type GuestFormInput } from './schemas.js';

const WRITTEN_KEYS = guests.writtenKeys;

/**
 * What the guest update endpoint accepts and refuses.
 *
 * All PII fields are optional: an empty input becomes `null` and clears the
 * stored value. `status` is an update-only field because it can only be chosen
 * once the guest exists.
 */
const GUEST_PAYLOAD: PayloadSpec = {
  required: [],
  optional: ['firstName', 'lastName', 'email', 'phone'],
  updateOnly: ['status'],
};

export function updateGuestPayload(
  values: GuestFormInput,
  version: number,
): Record<string, unknown> {
  const payload = updatePayload(values, GUEST_PAYLOAD, version);

  if (values.accountStatus !== undefined && values.accountStatus !== '') {
    payload.accountStatus = values.accountStatus;
  }

  if (values.address !== undefined && values.address.line1 !== '') {
    payload.address = values.address;
  }

  return payload;
}

export interface UpdateGuestVariables {
  readonly guestId: string;
  readonly version: number;
  readonly values: GuestFormInput;
}

/**
 * Updating a guest's status and PII.
 *
 * The PII is encrypted by the service; the portal only ever sends and stores
 * plaintext in the form while the request is in flight.
 */
export function useUpdateGuest(): UseMutationResult<unknown, unknown, UpdateGuestVariables> {
  return useApiMutation<UpdateGuestVariables, unknown>({
    mutationFn: async (api, { guestId, version, values }) => {
      await api.patch<unknown>(
        guests.endpoints.update(guestId),
        updateGuestPayload(values, version),
      );
    },
    invalidate: WRITTEN_KEYS,
  });
}
